import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../constants';
import { getUrlParameter, stripUrlParams } from '../lib/url';
import { logError, logWarn } from '../lib/log';

const POLL_MAX = 5;
const POLL_INTERVAL = 2000;
const TOAST_BY_TYPE = { success: toast.success, error: toast.error, warning: toast.warning };

const redirectTo = (url) => {
  try {
    if (window.top && window.top !== window) window.top.location.href = url;
    else window.location.href = url;
  } catch (error) {
    logWarn('Top-level redirect blocked, opening in _top:', error);
    window.open(url, '_top');
  }
};

export const useCheckout = ({ cart, clearCart, onPaid, t }) => {
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (!paymentStatus) return;
    const show = TOAST_BY_TYPE[paymentStatus.type] || toast.message;
    show(paymentStatus.message, { id: 'payment-status', duration: paymentStatus.type === 'pending' ? 8000 : 6000 });
  }, [paymentStatus]);

  const setStatus = useCallback((message, type) => setPaymentStatus({ message, type }), []);

  const completePaid = useCallback((message) => {
    clearCart();
    setPaymentLoading(false);
    setStatus(message, 'success');
    onPaid?.();
  }, [clearCart, onPaid, setStatus]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= POLL_MAX) {
      setStatus('Controllo pagamento scaduto. Controlla la tua email per la conferma.', 'error');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      if (data.payment_status === 'paid') { completePaid('Pagamento completato con successo! Grazie per il tuo acquisto.'); return; }
      if (data.status === 'expired') {
        setStatus('Sessione di pagamento scaduta. Riprova.', 'error');
        setPaymentLoading(false);
        return;
      }
      setStatus('Elaborazione pagamento in corso...', 'pending');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), POLL_INTERVAL);
    } catch (error) {
      logError('Payment status check failed:', error);
      setStatus('Errore nel controllo dello stato del pagamento. Riprova.', 'error');
      setPaymentLoading(false);
    }
  }, [completePaid, setStatus]);

  const checkReturnFromStripe = useCallback(() => {
    const sessionId = getUrlParameter('session_id');
    const payment = getUrlParameter('payment');
    const isBeautyKit = getUrlParameter('beautykit_payment') === 'true';

    if (sessionId && payment === 'success') {
      if (isBeautyKit) {
        localStorage.setItem('beautykit_payment_completed', 'true');
        localStorage.setItem('beautykit_session_id', sessionId);
        setStatus('Pagamento BeautyKit completato! Grazie per il tuo acquisto.', 'success');
      } else {
        setStatus('Verifica dello stato del pagamento...', 'pending');
        setPaymentLoading(true);
        pollPaymentStatus(sessionId);
      }
      stripUrlParams('session_id', 'payment', 'beautykit_payment');
    } else if (payment === 'cancelled') {
      setStatus(isBeautyKit ? 'Pagamento BeautyKit annullato. Puoi riprovare.' : 'Pagamento annullato. Puoi riprovare quando vuoi.', 'warning');
      stripUrlParams('payment', 'beautykit_payment');
    }
  }, [pollPaymentStatus, setStatus]);

  const initiateCartCheckout = useCallback(async (promoCode = '') => {
    if (!cart || cart.length === 0) { toast.warning(t('cart.empty')); return; }
    try {
      const { data } = await axios.post(`${API}/payments/checkout/session`, {
        package_type: 'cart',
        origin_url: window.location.origin,
        promo_code: promoCode || '',
        cart_items: cart.map((item) => ({
          id: item.id,
          name: item.name,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity, 10),
          line_color: item.line_color,
        })),
      });
      if (data.free) { completePaid(t('cart.free_success')); return; }
      if (!data.url) throw new Error('Nessun URL di checkout ricevuto');
      setTimeout(() => redirectTo(data.url), 100);
    } catch (error) {
      logError('Checkout failed:', error);
      setPaymentLoading(false);
      toast.error(`Errore checkout: ${error.response?.data?.detail || error.message}`);
    }
  }, [cart, completePaid, t]);

  return { paymentLoading, setPaymentLoading, initiateCartCheckout, checkReturnFromStripe };
};

export default useCheckout;
